import {
  aesGcmDecrypt,
  aesGcmEncrypt,
  deriveKeyFromRecoveryKey,
  deriveKeyFromVaultSecret,
  generateSalt,
} from './aes';
import { RECOVERY_KEY_BYTES, VAULT_SECRET_BYTES } from './constants';
import { arrayBufferToBase64, base64ToArrayBuffer } from './encoding';
import { randomBytes } from './random';
import { exportPrivateKeyPkcs8Pem, importPrivateKey, rsaOaepDecrypt } from './rsa';
import type { EncryptedPrivateKeyBlob } from './types';

export function generateVaultSecret(): string {
  return arrayBufferToBase64(randomBytes(VAULT_SECRET_BYTES).buffer as ArrayBuffer);
}

export function generateRecoveryKey(): string {
  return arrayBufferToBase64(randomBytes(RECOVERY_KEY_BYTES).buffer as ArrayBuffer);
}

async function encryptPrivateKeyMaterial(
  privateKey: CryptoKey | string,
  deriveKey: (salt: string) => Promise<CryptoKey>,
): Promise<EncryptedPrivateKeyBlob> {
  const privateKeyPem =
    typeof privateKey === 'string' ? privateKey : await exportPrivateKeyPkcs8Pem(privateKey);

  const salt = generateSalt();
  const aesKey = await deriveKey(salt);
  const encrypted = await aesGcmEncrypt(new TextEncoder().encode(privateKeyPem), aesKey);

  return {
    encryptedPrivateKey: encrypted.ciphertext,
    iv: encrypted.iv,
    salt,
  };
}

export async function encryptPrivateKey(
  privateKey: CryptoKey | string,
  vaultSecret: string,
): Promise<EncryptedPrivateKeyBlob> {
  return encryptPrivateKeyMaterial(privateKey, (salt) =>
    deriveKeyFromVaultSecret(vaultSecret, salt),
  );
}

export async function decryptPrivateKey(
  blob: EncryptedPrivateKeyBlob,
  vaultSecret: string,
): Promise<string> {
  const aesKey = await deriveKeyFromVaultSecret(vaultSecret, blob.salt);
  const plaintext = await aesGcmDecrypt(blob.encryptedPrivateKey, aesKey, blob.iv);
  return new TextDecoder().decode(plaintext);
}

export async function encryptWithRecoveryKey(
  privateKey: CryptoKey | string,
  recoveryKey: string,
): Promise<EncryptedPrivateKeyBlob> {
  return encryptPrivateKeyMaterial(privateKey, (salt) =>
    deriveKeyFromRecoveryKey(recoveryKey, salt),
  );
}

export async function decryptWithRecoveryKey(
  blob: EncryptedPrivateKeyBlob,
  recoveryKey: string,
): Promise<string> {
  const aesKey = await deriveKeyFromRecoveryKey(recoveryKey, blob.salt);
  const plaintext = await aesGcmDecrypt(blob.encryptedPrivateKey, aesKey, blob.iv);
  return new TextDecoder().decode(plaintext);
}

export async function decryptVaultPayload(
  encryptedPayload: string,
  ephemeralPrivateKey: CryptoKey | string,
): Promise<string> {
  const plaintext = await rsaOaepDecrypt(
    base64ToArrayBuffer(encryptedPayload),
    await importPrivateKey(ephemeralPrivateKey),
  );

  return new TextDecoder().decode(plaintext);
}
